import * as Sentry from '@sentry/vue'
import store from '@/vuex/store'
import router from '@/router'
// import EventBus from '@/eventBus'
import slideApi from '@/api/slide'
import { createPptActiveViewMachine } from '@/services/pptActiveViewMachine'
import UnleashService from '@/services/unleash'
import officeConstants from '@/constant/office'
import { createDebug } from '@/utils/debug'

// Toggle in the browser console with: localStorage.debug = 'aha:ppt:auto-start'
const log = createDebug('ppt:auto-start')
// AHA-46192 default-presentation flow. Toggle with: localStorage.debug = 'aha:ppt:default-presentation'
const logDefault = createDebug('ppt:default-presentation')

let initOffice = false
let viewMachine = null
let viewResizeObserver = null
let activeViewHandler = null
// Set right before each synchronous handleView() call so onEnterRead can log which
// signal source (ActiveViewChanged / ResizeObserver / init-query) triggered it.
let lastViewSource = 'unknown'
// Slide-focus polling: getSelectedDataAsync is the only per-instance signal that tracks
// the live slideshow position (incl. back-navigation). Once this instance's own slide id
// is captured, poll it so that returning to this slide re-asserts it as active.
let slidePollTimer = null
let savedHostPptSlideId = null
const SLIDE_POLL_INTERVAL_MS = 250
// Upper bound on how long add-in load waits for UnleashService.ready before routing
// anyway. `ready` only resolves on the client's 'ready' event, so a stalled proxy must
// not be allowed to hang load-time routing (incl. the flag-off auto-start path).
const UNLEASH_READY_TIMEOUT_MS = 3000
// let ws

// AHA-46192: the cross-frame "last selection" lives in a PowerPoint presentation
// TAG (PowerPointApi 1.3), NOT in Office document.settings or localStorage.
// - document.settings is keyed per add-in INSTANCE and is only shared between frames
//   created by DUPLICATING a slide (office-js #2765) — not across inserted frames.
// - localStorage is origin-scoped, so it leaks across every .pptx open in the browser.
// A presentation tag is document-wide on BOTH web and desktop and persists inside the
// .pptx itself, so it is exactly "the last presentation picked in THIS document" and is
// readable from any add-in frame. Tag keys are case-insensitive (stored uppercased).
const LAST_SELECTED_TAG_KEY = 'AHALASTSELECTEDPRESENTATION'

// AHA-46192: the last-selected store uses the TagCollection API, which was added in
// PowerPointApi 1.3. window.PowerPoint.run alone only proves 1.1, so also confirm the
// host supports 1.3 — on an older PowerPoint we skip cleanly instead of letting the
// tag calls throw inside PowerPoint.run and relying on the catch to swallow them.
const canUsePresentationTags = () => {
  if (!window.PowerPoint || !window.PowerPoint.run) {
    return false
  }
  try {
    return Boolean(window.Office?.context?.requirements?.isSetSupported('PowerPointApi', '1.3'))
  } catch (error) {
    return false
  }
}

// AHA-46192: gate the "default to last selected presentation" fallback.
const isPptDefaultPresentationEnabled = () => {
  return Boolean(store.getters['featureToggles/canUsePptDefaultPresentation'])
}

// A signed-out add-in frame lands on /pages/login|register; the add-in load flow must
// never hijack those. Centralised so every early-out shares one check.
const isAuthPage = () => {
  const { href } = window.location
  return href.indexOf('/pages/login') !== -1 || href.indexOf('/pages/register') !== -1
}

// AHA-46192: the tag stores ONLY the presentationId (a plain string), never the
// {presentationId, slideId} / {activePresentationId} shapes — the fallback always
// opens the whole presentation, so a slideId would only re-pin another frame's slide.
const saveLastSelectedPresentation = async (presentationId) => {
  if (!canUsePresentationTags()) {
    return
  }
  try {
    await window.PowerPoint.run(async (context) => {
      const { tags } = context.presentation
      if (presentationId) {
        tags.add(LAST_SELECTED_TAG_KEY, String(presentationId))
      } else {
        // delete() is a no-op when the key is absent, so no existence check is needed.
        tags.delete(LAST_SELECTED_TAG_KEY)
      }
      await context.sync()
    })
    logDefault('saved last-selected tag =', presentationId)
  } catch (error) {
    logDefault('save last-selected tag failed', error)
  }
}

// AHA-46192: forget the document's last-selected presentation. Called when the user
// deliberately navigates OUT of the editor (editor-header back), which full-reloads
// onto a non-/presentation/ URL. Clearing the tag first means the reload's fallback
// finds nothing and stays put, so the user can browse other presentations instead of
// being bounced straight back into the one they just left. Flag-gated + awaitable so
// the caller can finish the tag write before triggering the reload.
const clearLastSelectedPresentation = async () => {
  if (!isPptDefaultPresentationEnabled()) {
    return
  }
  logDefault('clearing last-selected tag (editor back)')
  await saveLastSelectedPresentation(null)
}

const loadLastSelectedPresentation = async () => {
  if (!canUsePresentationTags()) {
    return null
  }
  try {
    let raw = null
    await window.PowerPoint.run(async (context) => {
      const tag = context.presentation.tags.getItemOrNullObject(LAST_SELECTED_TAG_KEY)
      tag.load('value,isNullObject')
      await context.sync()
      raw = tag.isNullObject ? null : tag.value
    })
    logDefault('loaded last-selected tag =', raw)
    return raw || null
  } catch (error) {
    logDefault('load last-selected tag failed', error)
    return null
  }
}

const saveDataToSlidePowerPoint = async (key, value) => {
  if (window.Office && window.Office.context && window.Office.context.host) {
    // AHA-46192: mirror the dataSlidePPT selection into a global last-selected
    // key so a blank frame can fall back to it. Set mirrors set; null/empty
    // mirrors clear. Only for the dataSlidePPT key (so this never re-enters for
    // the mirror key), and only when the flag is on (flag off = old behaviour).
    if (key === 'dataSlidePPT' && isPptDefaultPresentationEnabled()) {
      // Mirror only the presentationId (from either shape); null clears the tag.
      const presentationId = value && (value.activePresentationId || value.presentationId)
      await saveLastSelectedPresentation(presentationId || null)
    }
    window.Office.context.document.settings.set(key, value)
    if (!value) {
      window.Office.context.document.settings.remove(key)
      window.Office.context.document.settings.set(key, '')
      window.Office.context.document.settings.saveAsync(() => {
      })
    }
    return new Promise((resolve, reject) => {
      window.Office.context.document.settings.saveAsync((asyncResult) => {
        if (asyncResult.status === window.Office.AsyncResultStatus.Failed) {
          reject()
        } else {
          resolve()
        }
      })
    })
  }
  return null
}

// const addDataToSelectedSlide = async (dataNumber) => {
//   try {
//     await window.PowerPoint.run(async (context) => {
//       const selection = context.presentation.getSelectedSlides()
//       selection.load('items, count')
//       await context.sync()

//       if (selection.count === 0) {
//         console.log('No slide selected! Please select a slide or object on slide.')
//         return
//       }

//       const slide = selection.items[0]
//       slide.load('index, tags')
//       await context.sync()

//       const myData = { value: dataNumber, slideIndex: slide.index }
//       slide.tags.add('myData', JSON.stringify(myData))
//       await context.sync()

//       console.log(`Data ${dataNumber} added to slide ${slide.index + 1}`)

//       // Update WebSocket
//       if (ws.readyState === WebSocket.OPEN) {
//         ws.send(`${slide.index}|${JSON.stringify(myData)}`)
//       }
//     })
//   } catch (error) {
//     console.error('Error adding data to selected slide:', error)
//   }
// }

// const initWS = () => {
//   ws = new WebSocket('ws://127.0.0.1:17890')

//   ws.onopen = () => {
//     console.log('WebSocket connected')
//   }

//   ws.onclose = () => {
//     console.log('WebSocket disconnected')
//     setTimeout(initWS, 1000)
//   }

//   ws.onerror = (err) => {
//     console.error('WebSocket error:', err)
//   }

//   ws.onmessage = (event) => {
//     console.log('Received:', event.data)
//     const [slideIndex, dataSlide] = event.data.split('|')
//     console.log('slideIndex, dataSlide', slideIndex, dataSlide)
//     const data = JSON.parse(dataSlide)
//     EventBus.$emit('ppt-active-slide-from-socket', data.value)
//   }
// }

const loadDataFromSlidePowerPoint = async (key) => {
  if (!window.Office) return null
  if (!window.Office.context) return null
  if (!window.Office.context.host) return null
  if (window.Office.context.platform === 'OfficeOnline') {
    store.commit('setIsOfficeOnline', true)
  }
  store.commit('setAddinPPTPlatform', window.Office.context.platform)
  return new Promise((resolve, reject) => {
    window.Office.context.document.settings.refreshAsync((asyncResult) => {
      if (asyncResult.status === window.Office.AsyncResultStatus.Failed) {
        reject()
      } else {
        const ahaSlideId = window.Office.context.document.settings.get(key)
        if (ahaSlideId !== null) {
          resolve(ahaSlideId)
        } else {
          resolve()
        }
      }
    })
  })
}

const setDataSlideInPowerpointToState = (ahaPresentationId, ahaSlideId) => {
  store.state.powerPointPresentationId = ahaPresentationId
  store.state.powerPointSlideId = ahaSlideId

  // if (ahaSlideId) {
  //   addDataToSelectedSlide(ahaSlideId)
  // }
}

const setDataPresentationInPowerpointToState = (ahaPresentationId) => {
  store.state.activePresentationPowerpointId = ahaPresentationId
}

const handleGetCorrentSlideIdByMultipleSession = async (presentationId, slideId) => {
  try {
    const resp = await slideApi.getCurrentSlideIdByOldSlideId({ presentationId, slideId })
    if (resp && resp.slideMap && resp.slideMap[slideId]) {
      const payload = {
        presentationId,
        slideId: resp.slideMap[slideId],
      }
      await saveDataToSlidePowerPoint('dataSlidePPT', payload)
      return payload
    }
  } catch (error) {
    return { presentationId, slideId }
  }
}

// AHA-46192: fall back to the last presentation selected in THIS document when a
// frame loads with no selection of its own. This is its OWN branch, not data
// substitution: the tag holds only a presentationId, so we just open that
// presentation's detail page — NOT present mode (no presenting/ppt-presentation
// query, no setDataPresentationInPowerpointToState), so it is not treated as a
// selected/presenting presentation. Flag-gated; handleOnLoadOffice awaits
// UnleashService.ready before calling, so the getter is populated. Flag off, tag
// absent, or PowerPointApi 1.3 unavailable => nothing opened (behaviour unchanged).
const openLastSelectedPresentationFallback = async () => {
  // Bail before the tag round-trip on auth pages / when the flag is off.
  if (!isPptDefaultPresentationEnabled() || isAuthPage()) {
    return
  }
  const presentationId = await loadLastSelectedPresentation()
  if (!presentationId) {
    return
  }
  if (window.location.href.indexOf('/presentation/') === -1) {
    logDefault('no local selection -> opening last-selected presentation', presentationId)
    router.replace({
      name: 'PresentationDetail',
      params: { id: presentationId },
    })
  }
}

const handleDataAfterLoadOffice = async () => {
  let dataSlidePPT = await loadDataFromSlidePowerPoint('dataSlidePPT')

  if (dataSlidePPT && dataSlidePPT.activePresentationId) {
    setDataPresentationInPowerpointToState(dataSlidePPT.activePresentationId)
    if (isAuthPage()) {
      return
    }
    if (window.location.href.indexOf('/presentation/') === -1) {
      router.replace({
        name: 'PresentationDetail',
        params: { id: dataSlidePPT.activePresentationId },
        query: { presenting: true, 'ppt-presentation': true },
      })
    }
    return
  }
  if (dataSlidePPT && dataSlidePPT.slideId && dataSlidePPT.presentationId) {
    dataSlidePPT = await handleGetCorrentSlideIdByMultipleSession(dataSlidePPT.presentationId, dataSlidePPT.slideId)
    setDataSlideInPowerpointToState(dataSlidePPT.presentationId, dataSlidePPT.slideId)
    if (window.location.href.indexOf('/presentation/') === -1) {
      if (isAuthPage()) {
        return
      }
      router.replace({
        name: 'PresentationDetail',
        params: { id: dataSlidePPT.presentationId },
        query: { presenting: true },
      })
    }
    return
  }
  // AHA-46192: no current selection of this frame's own -> fall back to the
  // document's last-selected presentation.
  await openLastSelectedPresentationFallback()
}

const reCheckActiveView = () => {
  window.Office?.context?.document?.getActiveViewAsync((result) => {
    store.commit('setActiveViewMode', result.value)
  })
}

const isAddinAutoStartEnabled = () => {
  const enableAddin = store.state.isEnableAddinPowerpoint
  const storeGetterFlag = store.getters['featureToggles/canUseAddinPowerpointAutoStart']
  // Windows desktop only. The active-view + ResizeObserver + slide-focus-poll flow
  // relies on the ActiveViewChanged 'edit' signal to stop polling; that signal is
  // flaky on Mac and never fires on the web (office-js #102), so on those platforms
  // the poll would never stop. Mirrors the Mac exclusion in shouldShowPowerpointPresenting.
  const isWindowsPlatform = store.state.addinPPTPlatform === officeConstants.PC
  const enabled = Boolean(enableAddin && storeGetterFlag && isWindowsPlatform)
  log('autoStartEnabled =', enabled, '| flag =', storeGetterFlag, '| platform =', store.state.addinPPTPlatform)
  return enabled
}

const getActiveViewValue = () => new Promise((resolve, reject) => {
  const doc = window.Office?.context?.document
  if (!doc) {
    // Office not ready yet — not an error; callers skip a falsy view.
    resolve(undefined)
    return
  }
  doc.getActiveViewAsync((result) => {
    const failed = window.Office?.AsyncResultStatus
      && result?.status === window.Office.AsyncResultStatus.Failed
    if (failed) {
      // A real API failure rejects so the state machine's fail-safe is reachable.
      reject(result?.error || new Error('getActiveViewAsync failed'))
      return
    }
    resolve(result?.value)
  })
})

// On entering 'read' (this instance's slide is being shown), ask PowerPoint which
// slide is currently selected in the live show. getSelectedDataAsync(SlideRange) is
// the only per-instance signal that tracks the slideshow position (incl. back-nav).
// Persist the host PPT slide id to the add-in settings and log it for debugging.
// Reads the currently-selected slide id in the live show via getSelectedDataAsync.
// Invokes onResult(selectedId, error, slides) — selectedId is null on failure.
const readSelectedSlideId = (onResult) => {
  const doc = window.Office?.context?.document
  const slideRange = window.Office?.CoercionType?.SlideRange
  if (!doc || !doc.getSelectedDataAsync || !slideRange) {
    onResult(null, 'office/getSelectedDataAsync not available')
    return
  }
  doc.getSelectedDataAsync(slideRange, (result) => {
    const failed = window.Office?.AsyncResultStatus
      && result?.status === window.Office.AsyncResultStatus.Failed
    if (failed) {
      onResult(null, result?.error?.message || 'failed')
      return
    }
    const slides = result?.value?.slides
    const selectedId = slides && slides[0] && slides[0].id
    onResult(selectedId, null, slides)
  })
}

// Polls the live selected slide. When it matches this instance's saved host id, the
// user has (re-)focused this slide, so re-assert it as active. autoStartActiveSlideFromView
// has its own guard, so it no-ops while this slide is already the active one.
const pollSelectedSlide = () => {
  readSelectedSlideId((selectedId, error) => {
    if (error) {
      log('poll error:', error)
      return
    }
    const isMatch = !!selectedId && !!savedHostPptSlideId && selectedId === savedHostPptSlideId
    log('poll selectedId =', selectedId, '| saved =', savedHostPptSlideId, '| match =', isMatch)
    if (isMatch) {
      store.dispatch('pptAddin/autoStartActiveSlideFromView')
    } else if (selectedId && savedHostPptSlideId && selectedId !== savedHostPptSlideId) {
      // PowerPoint has focused a different slide, so this instance lost focus.
      // Arm the re-start flag so the next time this slide regains focus we
      // re-assert it as the presenting slide even if presentation.activeSlide
      // still points at it (autoStartActiveSlideFromView reads this flag).
      store.commit('setPptSlideLostFocus', true)
    }
  })
}

const startSlideFocusPolling = () => {
  if (slidePollTimer !== null) return // already polling for this instance
  log('start poll')
  slidePollTimer = setInterval(pollSelectedSlide, SLIDE_POLL_INTERVAL_MS)
}

const stopSlideFocusPolling = () => {
  if (slidePollTimer === null) return
  clearInterval(slidePollTimer)
  slidePollTimer = null
  log('stop poll')
}

const captureActiveSlidePptId = () => {
  readSelectedSlideId((hostPptSlideId, error) => {
    if (error) {
      log('capture hostPptSlideId failed:', error)
      return
    }
    saveDataToSlidePowerPoint('hostPptSlideId', hostPptSlideId)
      .then(() => {
        savedHostPptSlideId = hostPptSlideId
        log('captured hostPptSlideId =', hostPptSlideId)
        startSlideFocusPolling()
        return null
      })
      .catch(() => {
        log('save hostPptSlideId failed =', hostPptSlideId)
      })
  })
}

const resyncActiveView = async (source = 'resync') => {
  try {
    const view = await getActiveViewValue()
    log('resync', source, '-> view =', view)
    if (view && viewMachine) {
      lastViewSource = source
      viewMachine.handleView(view)
    }
  } catch (error) {
    // Init/resize re-sync is best-effort; swallow to avoid unhandled rejections.
    log('resync', source, 'error:', error)
  }
}

const setupAutoStartViewMachine = () => {
  const officeDoc = window.Office?.context?.document
  const activeViewChanged = window.Office?.EventType?.ActiveViewChanged

  // Idempotent: if a prior machine/observer/handler/poll exists (e.g.
  // detectActiveViewChange runs again), tear them down first so we don't leak a
  // ResizeObserver, a duplicate ActiveViewChanged handler, or a stray poll timer.
  if (viewMachine) viewMachine.dispose()
  if (viewResizeObserver) {
    viewResizeObserver.disconnect()
    viewResizeObserver = null
  }
  if (officeDoc && activeViewChanged && activeViewHandler) {
    officeDoc.removeHandlerAsync(activeViewChanged, { handler: activeViewHandler })
    activeViewHandler = null
  }
  stopSlideFocusPolling()

  viewMachine = createPptActiveViewMachine({
    getActiveView: getActiveViewValue,
    commitView: (view) => store.commit('setActiveViewMode', view),
    onEnterRead: () => {
      log('read', lastViewSource)
      captureActiveSlidePptId()
      store.dispatch('pptAddin/autoStartActiveSlideFromView')
    },
  })

  // ActiveViewChanged 'read' is intentionally IGNORED for auto-start: it fires for EVERY
  // slide instance on "Play from Start" (Office issue #102), so it would wrongly start
  // non-focused slides. ResizeObserver + init-query + the poll track the actually-focused
  // slide. We DO act on 'edit' though — it's the reliable "slideshow ended / back to edit"
  // signal, used here to stop the wasteful slide-focus poll.
  if (officeDoc && activeViewChanged) {
    activeViewHandler = (args) => {
      if (args.activeView !== 'edit') return
      log('view -> edit, stop poll')
      lastViewSource = 'ActiveViewChanged'
      viewMachine.handleView('edit')
      stopSlideFocusPolling()
    }
    officeDoc.addHandlerAsync(activeViewChanged, activeViewHandler)
  }

  // Resize re-sync: catches view changes the event misses (Mac "play from current",
  // slideshow window resize). Guarded for environments without ResizeObserver.
  if (typeof ResizeObserver !== 'undefined') {
    viewResizeObserver = new ResizeObserver(() => {
      resyncActiveView('ResizeObserver')
    })
    viewResizeObserver.observe(document.documentElement)
  } else {
    log('no ResizeObserver')
  }

  // Init query: covers web (ActiveViewChanged never fires there) and cold-start
  // into a running show.
  resyncActiveView('init-query')
}

const detectActiveViewChange = () => {
  if (isAddinAutoStartEnabled()) {
    log('detect -> auto-start path')
    setupAutoStartViewMachine()
    return
  }
  log('detect -> legacy path')
  window.Office?.context?.document?.addHandlerAsync(
    window.Office.EventType.ActiveViewChanged,
    (args) => {
      store.commit('setActiveViewMode', args.activeView)
    },
  )
  reCheckActiveView()
}

const handleOnLoadOffice = async () => {
  initOffice = true
  if (window.Office && window.Office.context && window.Office.context.host) {
    store.state.isPowerPointAddIn = true
    if (window._historyCache) {
      window.history.replaceState = window._historyCache.replaceState
      window.history.pushState = window._historyCache.pushState
      window.history.back = window._historyCache.back
    }
    // Wait for Unleash to be ready so the store's feature-flag mirror is populated
    // before handleDataAfterLoadOffice() reads canUsePptDefaultPresentation (AHA-46192)
    // and before detectActiveViewChange() reads the auto-start flag. The add-in entry
    // from App.vue (non-/presentation/ route) has no other ready gate, so without this
    // the flag reads race the fetch. Placed after `initOffice = true` so the dedup
    // guard in resolveOffice still blocks the 2s setTimeout fallback from re-entering.
    // Bounded by a timeout: UnleashService.ready only resolves on the client's 'ready'
    // event, so a stalled proxy would otherwise hang add-in routing (including the
    // flag-off auto-start path) forever. On timeout we proceed with whatever flags are
    // mirrored so far — the same graceful-degradation the fetch-error path already gives.
    let readyTimer
    await Promise.race([
      UnleashService.ready,
      new Promise((resolve) => { readyTimer = setTimeout(resolve, UNLEASH_READY_TIMEOUT_MS) }),
    ])
    clearTimeout(readyTimer)
    handleDataAfterLoadOffice()
    detectActiveViewChange()

    try {
      // initWS()
    } catch (error) {
      console.log('cannot connected powerpoint socket', error)
      // do nothing
    }
  }
}

const loadOffice = async (source = 'unknown') => {
  try {
    // Office.js replaces the default Window.history methods of replaceState and pushState with null. This is done to support older Microsoft webviews and Office versions.
    // But our PowerPoint add-in needs PowerPoint 2019+, which is fully compatible with the new browser APIs.
    // This is the workaround to restore the original methods.
    window._historyCache = {
      replaceState: window.history.replaceState,
      pushState: window.history.pushState,
      back: window.history.back,
    }
    if (window.Office) {
      return window.Office
    }
    const loadOfficePromise = async () => {
      return new Promise((resolve, reject) => {
        const script1 = document.createElement('script')
        script1.onload = () => {
          const resolveOffice = async () => {
            if (!initOffice) {
              log('loaded office', source)
              await handleOnLoadOffice()
              resolve(window.Office)
            }
          }
          window.Office.onReady(() => {
            resolveOffice()
          })
          setTimeout(async () => {
            resolveOffice()
          }, 2000)
        }
        script1.onerror = reject
        script1.src = 'https://appsforoffice.microsoft.com/lib/1/hosted/office.js'
        document.head.appendChild(script1)
      })
    }
    await loadOfficePromise()
    return window.Office
  } catch (error) {
    Sentry.captureException(error)
    return null
  }
}

export default {
  loadOffice,
  saveDataToSlidePowerPoint,
  loadDataFromSlidePowerPoint,
  setDataSlideInPowerpointToState,
  setDataPresentationInPowerpointToState,
  handleDataAfterLoadOffice,
  clearLastSelectedPresentation,
  reCheckActiveView,
  detectActiveViewChange,
}
